"use client";

import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useWindowSize } from "hooks/use-window-size";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useDialog } from "@/hooks/use-dialog";
import { useLang } from "@/hooks/use-lang";
import { usePublicData } from "@/services/public-data";
import {
  UserLimitationsType,
  userLimitationsSchema,
} from "@/types/user-limitations";

import { DataList13F } from "ui/13f/data-list-13f";
import { IconInput } from "ui/core/input";

import { use13FDefaultSearch } from "../services/get-13f-default-search";
import { SearchDialogContent } from "./SearchDialogContent";

export const SearchDataList = () => {
  const { t, lang } = useLang();
  const { Dialog, openDialog, closeDialog } = useDialog();
  const router = useRouter();
  const { width } = useWindowSize();

  const {
    data: defaultSearchInvestors,
    isLoading: is13FDefaultSearchLoading,
    isError: is13FDefaultSearchError,
  } = use13FDefaultSearch();

  const { privateInvestors, isPrivateInvestorsLoading } = usePrivateInvestors();

  const handleDataListSelect = (investor: string) => {
    router.push(`/${lang}${links.my13FDetail({ investor })}`);
  };

  useEffect(() => {
    if (!width) return;
    if (width > 640) closeDialog();
  }, [closeDialog, width]);

  if (
    isPrivateInvestorsLoading ||
    is13FDefaultSearchLoading ||
    is13FDefaultSearchError
  )
    return null;

  const investors = defaultSearchInvestors.filter(
    (investor) => !privateInvestors.includes(investor.investor)
  );

  return (
    <>
      <DataList13F
        data={investors ?? []}
        className="z-10 hidden max-w-[387px] sm:block"
        onSelect={handleDataListSelect}
        aria-label="search 13F institution"
        role="search"
        lang={lang}
      />
      <button
        onClick={() => openDialog()}
        className="w-full max-w-xs sm:hidden"
        aria-label="open 13F institution search dialog"
      >
        <IconInput
          placeholder={t.my13F.overview.search13F}
          className="pointer-events-none bg-white bg-opacity-10 text-sm placeholder-opacity-0 ring-0 placeholder:text-white placeholder:text-opacity-80"
          leadingIcon={<MagnifyingGlassIcon className="h-5 w-5 text-white" />}
        />
      </button>
      <Dialog>
        <SearchDialogContent data={investors ?? []} closeDialog={closeDialog} />
      </Dialog>
    </>
  );
};

const usePrivateInvestors = () => {
  const { data: userLimitations, isLoading: isPrivateInvestorsLoading } =
    usePublicData<UserLimitationsType>({
      itemName: "user-limitations",
      schema: userLimitationsSchema,
    });
  const { profile } = useAuth();
  const name = profile?.name ?? "";

  const privateInvestorsData =
    userLimitations?.["13F-search-list-private-investors"]?.data;

  const privateInvestors =
    privateInvestorsData
      ?.flatMap(
        ({
          user,
          privateInvestor,
        }: {
          user: string[];
          privateInvestor: string[];
        }) =>
          user.find((user) => name.startsWith(user)) ? null : privateInvestor
      )
      .filter(Boolean) ?? [];

  return {
    privateInvestors,
    isPrivateInvestorsLoading,
  };
};
